<script>
  import { onMount } from 'svelte';

  let { rawImage, corners, onCornersChange } = $props();

  let containerEl = $state(null);
  let imageEl = $state(null);
  let localCorners = $state(null);
  let dragging = $state(null);
  let imageLoaded = $state(false);
  let scale = $state({ x: 1, y: 1, offsetX: 0, offsetY: 0 });

  // Initialize corners when props change
  $effect(() => {
    if (corners) {
      localCorners = JSON.parse(JSON.stringify(corners));
    }
  });

  function handleImageLoad() {
    imageLoaded = true;
    updateScale();
  }

  function updateScale() {
    if (!containerEl || !imageEl || !imageEl.naturalWidth) return;

    const containerRect = containerEl.getBoundingClientRect();
    const imgW = imageEl.naturalWidth;
    const imgH = imageEl.naturalHeight;

    // Calculate how image is displayed (object-fit: contain)
    const containerRatio = containerRect.width / containerRect.height;
    const imgRatio = imgW / imgH;

    let renderedW, renderedH, offsetX, offsetY;

    if (imgRatio > containerRatio) {
      renderedW = containerRect.width;
      renderedH = containerRect.width / imgRatio;
      offsetX = 0;
      offsetY = (containerRect.height - renderedH) / 2;
    } else {
      renderedH = containerRect.height;
      renderedW = containerRect.height * imgRatio;
      offsetX = (containerRect.width - renderedW) / 2;
      offsetY = 0;
    }

    scale = {
      x: renderedW / imgW,
      y: renderedH / imgH,
      offsetX,
      offsetY
    };
  }

  function toScreenCoord(point) {
    return {
      x: point.x * scale.x + scale.offsetX,
      y: point.y * scale.y + scale.offsetY
    };
  }

  function toImageCoord(screenX, screenY) {
    return {
      x: Math.round((screenX - scale.offsetX) / scale.x),
      y: Math.round((screenY - scale.offsetY) / scale.y)
    };
  }

  function handlePointerDown(corner, e) {
    e.preventDefault();
    dragging = corner;
    containerEl?.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e) {
    if (!dragging || !containerEl || !localCorners) return;

    const rect = containerEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const imgCoord = toImageCoord(x, y);

    // Clamp to image bounds
    const maxX = imageEl?.naturalWidth || 1000;
    const maxY = imageEl?.naturalHeight || 1000;
    imgCoord.x = Math.max(0, Math.min(maxX, imgCoord.x));
    imgCoord.y = Math.max(0, Math.min(maxY, imgCoord.y));

    localCorners = {
      ...localCorners,
      [dragging]: imgCoord
    };

    onCornersChange?.(localCorners);
  }

  function handlePointerUp(e) {
    if (dragging) {
      containerEl?.releasePointerCapture(e.pointerId);
      dragging = null;
    }
  }

  onMount(() => {
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  });

  // Calculate SVG path for corners
  function getCornerPath() {
    if (!localCorners || !imageLoaded) return '';
    const tl = toScreenCoord(localCorners.tl);
    const tr = toScreenCoord(localCorners.tr);
    const br = toScreenCoord(localCorners.br);
    const bl = toScreenCoord(localCorners.bl);
    return `M ${tl.x} ${tl.y} L ${tr.x} ${tr.y} L ${br.x} ${br.y} L ${bl.x} ${bl.y} Z`;
  }

  const cornerKeys = ['tl', 'tr', 'br', 'bl'];
</script>

<div
  class="corner-editor"
  bind:this={containerEl}
  onpointermove={handlePointerMove}
  onpointerup={handlePointerUp}
  onpointerleave={handlePointerUp}
  role="application"
  aria-label="Corner adjustment editor"
>
  <img
    bind:this={imageEl}
    src={rawImage}
    alt="Document"
    onload={handleImageLoad}
  />

  {#if imageLoaded && localCorners}
    <svg class="corner-overlay">
      <!-- Semi-transparent overlay outside corners -->
      <defs>
        <mask id="cornerMask">
          <rect width="100%" height="100%" fill="white" />
          <path d={getCornerPath()} fill="black" />
        </mask>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="rgba(0,0,0,0.5)"
        mask="url(#cornerMask)"
      />

      <!-- Corner outline -->
      <path
        d={getCornerPath()}
        fill="none"
        stroke="#22c55e"
        stroke-width="2"
      />

      <!-- Draggable corner handles -->
      {#each cornerKeys as key}
        {@const pos = toScreenCoord(localCorners[key])}
        <circle
          cx={pos.x}
          cy={pos.y}
          r={dragging === key ? 20 : 16}
          fill={dragging === key ? '#22c55e' : 'white'}
          stroke="#22c55e"
          stroke-width="3"
          class="corner-handle"
          role="slider"
          tabindex="0"
          aria-label="Corner {key}"
          aria-valuenow={Math.round(localCorners[key].x)}
          onpointerdown={(e) => handlePointerDown(key, e)}
        />
      {/each}
    </svg>
  {/if}
</div>

<style>
  .corner-editor {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    touch-action: none;
    user-select: none;
  }

  .corner-editor img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .corner-overlay {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .corner-handle {
    pointer-events: auto;
    cursor: grab;
    transition: r 0.15s ease, fill 0.15s ease;
  }

  .corner-handle:active {
    cursor: grabbing;
  }
</style>
